// Insurance A/R — lists-at-scale mock (00-ds-09, D148). Deterministic, seeded
// rows so the three A/R lists can actually load-more, group and page in the
// browser. Real data is server-side (§8); this only exists for the demo.
//
//  - generateLateExtra(): a bigger "past the payer's usual time" queue so the
//    Late list's "Show 50 more" is demoable (§9: 15–90 open).
//  - generateOntime(): a big "within the payer's usual time" queue, grouped by
//    payer in the UI (§9: 150–900 open).
//  - generateWrittenOffHistory(): ~400 written-off claims across ~24 months for
//    the Written-off history table's numbered pages (§9: 120–700 after 2 years).

import {
  generate,
  pick,
  int,
  money,
  fullName,
  MOCK_NOW,
  PAYERS,
  CDT,
} from '@/components/lists';
import type { ArClaim, WriteOffRow } from './types';
import { nextCheckDate, scheduledLateTries } from './schedule';
import { canonPayerName, payerShort, usualDaysFor, usuallyWords } from './payers';
import { AR_TODAY_ISO, isoLabel, isoPlusDays } from './utils';
import { getFlagSettings } from './flagSettings';
import { PMS } from '@/features/shared/pms';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A "Mon D" (and ", YYYY" when not the mock year) label for `daysBack` before MOCK_NOW. */
function dayLabel(daysBack: number): string {
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - daysBack);
  const base = `${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}`;
  return d.getFullYear() === MOCK_NOW.getFullYear() ? base : `${base}, ${String(d.getFullYear())}`;
}

function dayIso(daysBack: number): string {
  const d = new Date(MOCK_NOW);
  d.setDate(d.getDate() - daysBack);
  return d.toISOString().slice(0, 10);
}

function memberId(r: () => number): string {
  const pre = pick(r, ['AET', 'DDX', 'CIG', 'MET', 'UHC', 'GRD', 'SL', 'BC', 'HUM']);
  return `${pre}${String(int(r, 10000000, 99999999))}`;
}

/** Rebuild a claim id that's stable for a given generated index + prefix. */
function claimId(prefix: string, i: number): string {
  return `${prefix}-${String(10000 + i)}`;
}

// ── Dates for A/R rows: counted to the A/R mock "today" (Oct 15), so "9 days since sent" and the
// sent date agree with the Dashboard's expected-pay dates. ──
function arLabel(daysBack: number): string {
  return isoLabel(isoPlusDays(AR_TODAY_ISO, -daysBack));
}
function arIso(daysBack: number): string {
  return isoPlusDays(AR_TODAY_ISO, -daysBack);
}
/** The visit is 1–3 days before the claim was sent (the aging cards count from the visit, D203). */
function visitGap(i: number): number {
  return 1 + (i % 3);
}

// ── Within the payer's usual time (big queue, grouped by payer in the UI) ──
//
// The queue is built from per-payer segments so the Dashboard's "Money coming in" figures come
// out of the rows (H11): each segment is scaled to its target total at cent precision. Targets
// are mock calibration only; every figure on screen is summed from the resulting rows.
interface OntimeSegment {
  payer: string;
  count: number;
  /** Total these generated claims add up to (the curated rows of the same payer are on top). */
  target: number;
  /** Days since sent: [min, max]. */
  days: [number, number];
}

const ONTIME_SEGMENTS: OntimeSegment[] = [
  // Expected within the next 30 days (sent + the payer's usual time falls between today and +30).
  { payer: 'UnitedHealthcare', count: 48, target: 41200, days: [1, 20] },
  { payer: 'Delta Dental of Texas', count: 44, target: 38950 - 164, days: [1, 13] },
  { payer: 'MetLife', count: 38, target: 33120, days: [1, 13] },
  { payer: 'Cigna', count: 35, target: 29870, days: [1, 13] },
  { payer: 'Guardian', count: 27, target: 24060 - 1015, days: [1, 13] },
  { payer: 'Aetna', count: 25, target: 22500 - 612, days: [1, 20] },
  { payer: 'BlueCross BlueShield of Texas', count: 25, target: 22000, days: [1, 20] },
  { payer: 'United Concordia', count: 24, target: 21000, days: [1, 27] },
  { payer: 'Principal', count: 27, target: 23000, days: [1, 13] },
  { payer: 'Ameritas', count: 24, target: 20500, days: [1, 20] },
  { payer: 'Anthem Blue Cross', count: 25, target: 21200, days: [1, 13] },
  { payer: 'GEHA', count: 24, target: 21000, days: [1, 20] },
  // Sun Life has no usual pay time yet: expected date unknown, so not in the 30-day figure.
  { payer: 'Sun Life', count: 14, target: 12600, days: [1, 50] },
  // Humana usually pays in about 6 weeks and these were sent in the last 11 days: expected more
  // than 30 days from now, so in neither the 30-day figure nor Overdue.
  { payer: 'Humana', count: 41, target: 35344.89, days: [1, 11] },
];

const FALLBACK_SEGMENT: OntimeSegment = { payer: 'Cigna', count: 0, target: 1, days: [1, 13] };

export const IAR_ONTIME_GEN_COUNT = ONTIME_SEGMENTS.reduce((s, g) => s + g.count, 0);

/** Scale `raw` amounts to add up to `target` exactly (cents), keeping every amount positive. */
function scaleToTarget(raw: number[], target: number): number[] {
  const sum = raw.reduce((s, v) => s + v, 0);
  const scaled = raw.map((v) => Math.max(1, Math.round(((v * target) / sum) * 100) / 100));
  const drift = Math.round((target - scaled.reduce((s, v) => s + v, 0)) * 100) / 100;
  const last = scaled.length - 1;
  scaled[last] = Math.round(((scaled[last] ?? 0) + drift) * 100) / 100;
  return scaled;
}

export function generateOntime(): ArClaim[] {
  const segOf: OntimeSegment[] = ONTIME_SEGMENTS.flatMap((g) => Array.from({ length: g.count }, () => g));
  const raw = generate(IAR_ONTIME_GEN_COUNT, 0x51a3, (r, i) => ({
    seg: segOf[i] ?? ONTIME_SEGMENTS[0] ?? FALLBACK_SEGMENT,
    name: fullName(r),
    proc: pick(r, CDT),
    amount: money(r, 80, 1680),
    daysRoll: r(),
    member: memberId(r),
    dentist: pick(r, ['Dr. Emily Carter', 'Dr. Raymond Poe']),
  }));
  // Scale each payer's amounts to its target.
  const amounts = new Map<string, number[]>();
  for (const g of ONTIME_SEGMENTS) {
    const mine = raw.filter((x) => x.seg === g).map((x) => x.amount);
    amounts.set(g.payer, scaleToTarget(mine, g.target));
  }
  const used = new Map<string, number>();
  return raw.map((x, i): ArClaim => {
    const payerName = canonPayerName(x.seg.payer);
    const short = payerShort(payerName);
    const first = x.name.split(' ')[0] ?? x.name;
    const k = used.get(x.seg.payer) ?? 0;
    used.set(x.seg.payer, k + 1);
    const billed = amounts.get(x.seg.payer)?.[k] ?? x.amount;
    const [dMin, dMax] = x.seg.days;
    const days = dMin + Math.floor(x.daysRoll * (dMax - dMin + 1));
    const gap = visitGap(i);
    const sent = arLabel(days);
    const usual = usualDaysFor(payerName);
    const id = claimId('AR-O', i);
    const received = days >= 2;
    const headline =
      usual !== undefined
        ? `${short} usually pays in ${usuallyWords(usual)}. ${String(days)} days so far.`
        : `${short} has no usual pay time yet. ${String(days)} days so far.`;
    return {
      id,
      patient: x.name,
      firstName: first,
      payer: { name: payerName, short },
      memberId: x.member,
      dentist: x.dentist,
      procedure: `${x.proc.desc} (${x.proc.code})`,
      visit: `visit ${arLabel(days + gap)}`,
      sentDisplay: sent,
      receivedDisplay: received ? `received by ${short} ${sent}` : `sent ${sent}`,
      billed,
      balance: billed,
      group: 'ontime',
      days,
      serviceDays: days + gap,
      sentIso: arIso(days),
      ...(usual !== undefined ? { usualDays: usual } : {}),
      status: {
        kind: 'ontime',
        headline,
        bodyLines: [
          received
            ? `Received by ${short} and in process. Nothing is needed from you yet.`
            : `Accepted by the clearinghouse. ${short} hasn’t confirmed receipt yet.`,
        ],
      },
      rowBadge: 'none',
      rowLine: received ? `In process at ${short}` : `Sent to ${short}; awaiting receipt`,
      rowMeta:
        usual !== undefined
          ? `${String(days)} days since sent · ${short} usually pays in ${usuallyWords(usual)}`
          : `${String(days)} days since sent · no usual pay time for ${short} yet`,
      tries: [
        { date: sent, icon: 'ok', head: 'Sent.', body: `Accepted by the clearinghouse.${received ? ` ${short} received it the same day.` : ''}`, logId: `${id.toLowerCase()}-1` },
      ],
      history: `History: sent by ELVA ${sent}`,
    };
  });
}

// ── Past the payer's usual time (bigger late queue for load-more) ──
export const IAR_LATE_GEN_COUNT = 63;

/** Payers with a usual pay time: a claim of 31+ days is past it. */
const LATE_PAYERS = PAYERS.filter((p) => p !== 'Sun Life' && p !== 'Humana');

/**
 * Late because of the practice's 60-day flag, not the payer's usual time (D311): these payers have
 * no usual pay time yet (Sun Life), so only the unpaid threshold can make their claims late.
 */
const FLAG_LATE_INDEXES = new Set<number>([0, 11, 28, 32]);

export function generateLateExtra(): ArClaim[] {
  const LIMIT = 3;
  const flagDays = getFlagSettings().unpaidDays;
  return generate(IAR_LATE_GEN_COUNT, 0x7c21, (r, i): ArClaim => {
    const flagged = FLAG_LATE_INDEXES.has(i);
    const rolledPayer = canonPayerName(pick(r, LATE_PAYERS));
    const payerName = flagged ? 'Sun Life' : rolledPayer;
    const short = payerShort(payerName);
    const name = fullName(r);
    const first = name.split(' ')[0] ?? name;
    const proc = pick(r, CDT);
    const billed = money(r, 120, 1850);
    const days = int(r, 31, 138);
    const gap = visitGap(i);
    const sent = arLabel(days);
    // Generated claims stay under the limit; the curated Sana/Victor are the
    // ones at it. Lowering the limit in Settings moves more of them over.
    const round = days > 55 ? 2 : 1;
    const id = claimId('AR-L', i);
    const deadline = `No filing deadline at risk: ${short} received the claim on ${sent}.`;
    // Every round shows as a dated status-check on the real schedule (§7).
    const tries = scheduledLateTries(id.toLowerCase(), short, sent, round, days + i);
    const usual = usualDaysFor(payerName);
    const base = {
      id,
      patient: name,
      firstName: first,
      payer: { name: payerName, short },
      memberId: memberId(r),
      dentist: pick(r, ['Dr. Emily Carter', 'Dr. Raymond Poe']),
      procedure: `${proc.desc} (${proc.code})`,
      visit: `visit ${arLabel(days + gap)}`,
      sentDisplay: sent,
      receivedDisplay: `received by ${short} ${sent}`,
      billed,
      balance: billed,
      group: 'late' as const,
      days,
      serviceDays: days + gap,
      sentIso: arIso(days),
      ...(usual !== undefined ? { usualDays: usual } : {}),
      round: { n: round, limit: LIMIT },
      tries,
      history: `History: sent by ELVA ${sent} · follow-ups by ELVA`,
    };
    const next = nextCheckDate(days + i);
    return {
      ...base,
      nextCheck: next,
      status: {
        kind: 'late',
        headline: flagged
          ? `${short} hasn’t paid in ${String(days)} days. That’s past your ${String(flagDays)}-day flag.`
          : `${short} hasn’t paid in ${String(days)} days. It’s past ${short}’s usual time.`,
        latest: `${short} says the claim is still in process. ELVA has been following up.`,
        next: `ELVA checks again on ${next}. That’s follow-up ${String(round + 1)} of ${String(LIMIT)}.`,
        deadline,
      },
      rowBadge: 'late',
      rowLine: flagged
        ? `${short} has no usual pay time yet; ELVA is following up`
        : `${short} is past its usual time; ELVA is following up`,
      rowMeta: `${String(days + gap)} days by date of service · ${String(days)} days since sent · follow-up ${String(round)} of ${String(LIMIT)} · next check ${next}`,
    };
  });
}

// ── Written-off history (numbered pages, ~24 months) ──
export const IAR_WRITTEN_OFF_GEN_COUNT = 400;

const WHY = [
  'Filed too late: past the payer’s filing deadline',
  'Denied and not appealed',
  'Small balance, not worth chasing',
  'Patient’s coverage had ended on the date of service',
  'Duplicate of a claim already paid',
  'Non-covered procedure under the plan',
  'Member not found after repeated resubmission',
];
const BY = [
  'Ava Thompson · Lone Star Billing',
  'Maria Gomez · the practice',
  `In ${PMS} by Maria Gomez`,
  'ELVA · after the follow-up limit',
];

/**
 * ~400 written-off claims dated 20–720 days back (so none land in the current
 * month — "this month" is owned by the live store rows, keeping the figure card
 * and the default window in agreement).
 */
export function generateWrittenOffHistory(): WriteOffRow[] {
  return generate(IAR_WRITTEN_OFF_GEN_COUNT, 0x2bd9, (r, i): WriteOffRow => {
    const daysBack = int(r, 20, 720);
    const payer = canonPayerName(pick(r, PAYERS));
    const name = fullName(r);
    const visitBack = daysBack + int(r, 14, 120);
    const recordId = `C-${String(500000 + i)}`;
    return {
      id: `WO-${recordId}`,
      recordId,
      dateDisplay: dayLabel(daysBack),
      iso: dayIso(daysBack),
      patient: name,
      visit: `visit ${dayLabel(visitBack)}`,
      payer,
      why: pick(r, WHY),
      by: pick(r, BY),
      amount: money(r, 24, 940),
    };
  });
}
